import React from "react";
import { Card, CardContent } from "../ui/card";
import { cn } from "../../lib/utils";

/**
 * Standard title + description for a notifications sub-page.
 * @param {Object} props
 * @param {string} props.title
 * @param {string} props.description
 * @returns {JSX.Element}
 */
export function NotificationTabHeader({ title, description }) {
  return (
    <div className="mb-6">
      <h2 className="text-xl font-semibold tracking-tight text-text-primary sm:text-2xl">{title}</h2>
      <p className="mt-1 text-sm leading-relaxed text-text-secondary sm:text-base">{description}</p>
    </div>
  );
}

/**
 * Centered empty / placeholder state inside the tab content card.
 * @param {Object} props
 * @param {string} props.title
 * @param {string} [props.subtitle]
 * @param {string} [props.className]
 * @returns {JSX.Element}
 */
export function NotificationEmptyState({ title, subtitle, className }) {
  return (
    <Card
      className={cn("border-dashed border-ui-border bg-ui-muted shadow-none", className)}
    >
      <CardContent className="px-4 py-10 text-center sm:px-8">
        <p className="text-lg text-text-secondary">{title}</p>
        {subtitle ? <p className="mt-2 text-sm text-text-muted">{subtitle}</p> : null}
      </CardContent>
    </Card>
  );
}

/**
 * Error message panel for a tab.
 * @param {Object} props
 * @param {string} props.message
 * @returns {JSX.Element}
 */
export function NotificationErrorPanel({ message }) {
  return (
    <Card className="border-ui-border bg-ui-muted shadow-none">
      <CardContent className="p-4">
        <p className="text-sm text-error" role="alert">
          {message}
        </p>
      </CardContent>
    </Card>
  );
}

/**
 * A single “request” row: bordered card, used for connection and affiliation items.
 * @param {Object} props
 * @param {string} [props.className]
 * @param {React.ReactNode} props.children
 * @returns {JSX.Element}
 */
export function NotificationRequestCard({ className, children }) {
  return (
    <Card
      className={cn(
        "overflow-hidden border-ui-border transition-colors hover:border-accent",
        "shadow-profile-sm",
        className
      )}
    >
      <CardContent className="p-4 sm:p-5">{children}</CardContent>
    </Card>
  );
}
